import React, { useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useBundle } from '@amzn/react-arb-tools';
import { validateEmptyTextInput, calculateDaysPassedToDate } from '@amzn/sss-website-theme';

import { COMMON_BUNDLE_NAME } from 'src/constants';
import { applyDateMask, getDateFormat, parseDateInput } from 'src/helpers/formatting/date-format-helpers';
import { isEmpty, removeNumberDecoration } from 'src/helpers/formatting/formatting-helpers';
import { t } from 'src/helpers/translations/translation-helpers';

// So users can't input less than 25 as the desired amount
const MIN_QUANTITY = 25;

// So users can't input more than 1000 as the desired amount
const MAX_QUANTITY = 1000;

enum REQUEST_CREDITS_FORM_KEY {
  QUANTITY = 'quantity',
  DATE = 'date',
}

export type CheckoutStep1FormValues = {
  [REQUEST_CREDITS_FORM_KEY.QUANTITY]: string;
  [REQUEST_CREDITS_FORM_KEY.DATE]: string;
};

interface UseCheckoutStep1FormProps {
  showBundles?: boolean;
  productBundleFactor?: number;
  marketplaceId?: string;
}

export const useCheckoutStep1Form = ({
  showBundles = false,
  productBundleFactor = 1,
  marketplaceId,
}: UseCheckoutStep1FormProps = {}) => {
  const [commonBundle] = useBundle(COMMON_BUNDLE_NAME);
  const [dateIsAfter90Days, setDateIsAfter90Days] = useState(false);
  const dateFormat = getDateFormat(marketplaceId);

  const form = useForm<CheckoutStep1FormValues>({
    reValidateMode: 'onSubmit',
  });

  const {
    clearErrors,
    formState: { errors },
    getValues,
    register,
    setValue,
    resetField,
  } = form;

  const setQuantityInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;

      if (value !== '' && !/[0-9]/.test(value)) {
        event.preventDefault();
        return;
      }

      const addCommas = (input: string) => input.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const removeNonNumeric = (input: string) => {
        const cleaned = input.replace(/[^\d.-]/g, '');
        return parseFloat(cleaned).toString();
      };
      if (value) {
        setValue(REQUEST_CREDITS_FORM_KEY.QUANTITY, addCommas(removeNonNumeric(value)));
      } else {
        resetField(REQUEST_CREDITS_FORM_KEY.QUANTITY);
      }
      clearErrors(REQUEST_CREDITS_FORM_KEY.QUANTITY);
    },
    [clearErrors, setValue, resetField]
  );

  const validateQuantityInput = useCallback(
    (quantity: string) => {
      const textInputValidation = validateEmptyTextInput(quantity, t(commonBundle, 'requiredField_label'));
      if (textInputValidation !== true) {
        return textInputValidation;
      }

      const rawQuantity = removeNumberDecoration(quantity);

      // Calculate dynamic limits based on bundle configuration
      const minQuantity = showBundles ? productBundleFactor : MIN_QUANTITY;
      // Ensure we don't hit upper limit of digital orders (limit of 1000)
      const maxQuantity = showBundles && productBundleFactor ? MAX_QUANTITY * productBundleFactor : MAX_QUANTITY;

      // Minimum quantity validation
      if (rawQuantity < minQuantity) {
        return t(commonBundle, 'checkout_minQuantityValidation', {
          minQuantity,
        });
      }

      // Maximum quantity validation
      if (rawQuantity > maxQuantity) {
        return t(commonBundle, 'checkout_maxQuantityValidation', {
          maxQuantity: maxQuantity.toLocaleString(),
        });
      }

      return true;
    },
    [commonBundle, showBundles, productBundleFactor]
  );

  const quantityInputFormProps = useMemo(
    () => ({
      ...register(REQUEST_CREDITS_FORM_KEY.QUANTITY, {
        required: t(commonBundle, 'requiredField_label'),
        validate: validateQuantityInput,
      }),
      inputType: 'text',
      id: REQUEST_CREDITS_FORM_KEY.QUANTITY,
      value: getValues(REQUEST_CREDITS_FORM_KEY.QUANTITY) ?? '',
      onChange: setQuantityInput,
      errors: errors[REQUEST_CREDITS_FORM_KEY.QUANTITY],
    }),

    // since eslint asks form.formState to be removed but we actually require it
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commonBundle, form.formState, getValues, register, setQuantityInput, validateQuantityInput]
  );

  const setDateInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      const maskedDate = applyDateMask(value);

      const parsedDate = parseDateInput(maskedDate, dateFormat);
      if (parsedDate) {
        // Convert to ISO for calculateDaysPassedToDate
        const isoDate = parsedDate.toISOString().slice(0, 10);
        const daysPassed = calculateDaysPassedToDate(isoDate);
        setDateIsAfter90Days(daysPassed ? daysPassed > 90 : false);
      } else {
        setDateIsAfter90Days(false);
      }

      setValue(REQUEST_CREDITS_FORM_KEY.DATE, maskedDate);
      clearErrors(REQUEST_CREDITS_FORM_KEY.DATE);
    },
    [clearErrors, setValue, dateFormat]
  );

  const validateDateInput = (date: string) => {
    const textInputValidation = validateEmptyTextInput(date, t(commonBundle, 'requiredField_label'));
    if (textInputValidation !== true) {
      return textInputValidation;
    }

    const parsedDate = parseDateInput(date, dateFormat);
    if (!parsedDate) {
      return t(commonBundle, 'checkout_invalidDateFormat');
    }

    const isoDate = parsedDate.toISOString().slice(0, 10);
    const daysPassed = calculateDaysPassedToDate(isoDate);

    // Min date validation
    if (daysPassed <= 0) {
      return t(commonBundle, 'checkout_minDateValidation');
    }

    // Max date validation
    if (daysPassed > 365) {
      return t(commonBundle, 'checkout_maxDateValidation');
    }

    return true;
  };

  const dateInputFormProps = useMemo(
    () => ({
      ...register(REQUEST_CREDITS_FORM_KEY.DATE, {
        required: t(commonBundle, 'requiredField_label'),
        validate: validateDateInput,
      }),
      inputType: 'text',
      placeholder: dateFormat,
      id: REQUEST_CREDITS_FORM_KEY.DATE,
      value: getValues(REQUEST_CREDITS_FORM_KEY.DATE) ?? '',
      onChange: setDateInput,
      errors: errors[REQUEST_CREDITS_FORM_KEY.DATE],
    }),
    // since eslint asks form.formState to be removed but we actually require it
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [commonBundle, form.formState, getValues, register, setDateInput, dateFormat]
  );

  const formIsCompleted = useMemo(
    () => !isEmpty(quantityInputFormProps.value) && !isEmpty(dateInputFormProps.value),
    [quantityInputFormProps, dateInputFormProps]
  );

  const formHasErrors = useMemo(() => Object.keys(errors).length > 0, [errors]);

  return {
    ...form,
    quantityInputFormProps,
    dateInputFormProps,
    formIsCompleted,
    formHasErrors,
    validateDateInput,
    validateQuantityInput,
    dateIsAfter90Days,
    dateFormat,
  };
};
